// *Saved views* on the Coordinator Workbench (B10 slice S21; plan D6, D15; CR-051 "… saved views, grouping, sorting,
// security-trimmed drill-down and exports."; CR-084; DC-155 "Filters, saved views and grouping as the chip row.").
//
// The slot contract with B5 (C-B10-13): the Workbench is B5's screen, so this mounts in its filter row through B5's
// Workbench slot (`registerWorkbenchSlot`, ./register.ts) as <WorkbenchViewsSlot>: `current` is the page's parsed URL
// filter (B5's `WorkbenchQuery`, the same shape as ./query.ts's) and `onApply` is the slot's `apply`, which opens the
// Workbench under another query, so a saved view is a query and never a second read. It is S19's shared select on surface
// `workbench`: *Save view* stores the current filter as a spec in `scheduling.workbench`'s own input keys (a filter,
// never rows; scope is never stored, so a view returns the viewer's current scope), and choosing a view applies its
// spec as the Workbench's URL filter. The keys the server refused for this viewer now (S10's `dropped`) and the keys the
// Workbench's URL cannot carry are both handed to `onApply`, so nothing is widened silently.

import type { ComponentProps, ComponentType } from "react";
import type { WorkbenchSlotProps } from "@/views/scheduling/slots";
import { SavedViewsSelect } from "@/views/b10/lib/savedViews";
import { queryOfSpec, specOfQuery, workbenchHref, WORKBENCH_SURFACE, type WorkbenchQuery } from "./query";

type SelectProps = ComponentProps<typeof SavedViewsSelect>;

export interface WorkbenchViewsProps {
  /** The Workbench's current filter, as its URL carries it. */
  current: Partial<WorkbenchQuery>;
  /** Applies a chosen view's filter; `dropped` names the spec keys left out. Default: open the Workbench under it. */
  onApply?: ((next: WorkbenchQuery, dropped: readonly string[]) => void) | undefined;
  /** The hotspot key of the select (default `sv.workbench`). */
  hs?: string | undefined;
  /** The saved-views control: S19's select; a test passes a stand-in. */
  select?: ComponentType<SelectProps> | undefined;
}

/** Opens the Workbench under a filter through the shell's router (loaded on use; this module renders on the server too). */
function openWorkbench(next: WorkbenchQuery): void {
  void import("@/router").then(({ go }) => go(workbenchHref(next)));
}

/** A chosen view's spec as the Workbench filter it applies, with every key left out (the server's first, then the URL's). */
export function applyView(
  spec: Readonly<Record<string, unknown>>,
  serverDropped: readonly string[],
): { query: WorkbenchQuery; dropped: string[] } {
  const { query, dropped } = queryOfSpec(spec);
  return { query, dropped: [...new Set([...serverDropped, ...dropped])] };
}

export function WorkbenchViews({ current, onApply, hs = "sv.workbench", select }: WorkbenchViewsProps) {
  const Select: ComponentType<SelectProps> = select ?? SavedViewsSelect;
  const apply: (next: WorkbenchQuery, dropped: readonly string[]) => void = onApply ?? openWorkbench;
  return (
    <Select
      surface={WORKBENCH_SURFACE}
      current={specOfQuery(current)}
      onApply={(spec, dropped) => {
        const next = applyView(spec, dropped);
        apply(next.query, next.dropped);
      }}
      hs={hs}
    />
  );
}

/** The Workbench's filter-row slot: the page's query is the current filter, and a chosen view goes through its `apply`.
 * The keys left out are not applied (the filter narrows to what the URL carries; nothing is widened). */
export function WorkbenchViewsSlot({ query, apply }: WorkbenchSlotProps) {
  return <WorkbenchViews current={query} onApply={(next) => apply(next)} />;
}
